import type { ReactNode } from 'react'
import type { Partner } from '@shared/types'
import type { SessionRole } from '../lib/api.js'

/**
 * LHS navigation shell (decision 30).
 *
 * The "Acting as" picker is decision 34 in its working form: a Wave9 admin
 * chooses which partner to work as, and that choice is SERVER state — switching
 * calls /api/act-as. The audit trail keeps the real staff identity throughout.
 */

export type Route =
  | 'cockpit' | 'catalogue' | 'quotes' | 'builder' | 'library' | 'templates' | 'costlog' | 'renewals'
  /** Decision 211: distributor quotes imported into the portal — staff only. */
  | 'supplierQuotes'
  /** Decision 183: agent claimable statements — the agent's own, and admin's view of every agent. */
  | 'commissions'
  /** Decision 209: the Needs-attention page (was a cockpit panel). */
  | 'attention'
  /** Decision 205: the monthly board slide — staff only. */
  | 'boardPack'
  /** Decision 125: the template editor — the builder over a workspace row. */
  | 'templateEditor'
  | 'snippets'
  /** Decision 135: the T&Cs library gets its own Admin page. */
  | 'terms'
  | 'partners' | 'productLines' | 'organisation' | 'jobs' | 'readiness'
  /** Decision 213: the Xero connection + purchase-order settings. */
  | 'xero'
  /** Decision 223: the billing queue + review screen — admin key only (Mark 2026-09-14 pm: "the billing page should be in the admin section and RBAC applied"). */
  | 'billing'
  /** Decision 224: Admin → Billing variations, by month. */
  | 'billingVariations'

// Decision 123: the flat list read as "thirteen destinations" to new users —
// grouped into the places you WORK (daily) and the places you MANAGE THINGS
// you reuse (library). Nothing moved routes; this is presentation only.
const DAILY_NAV: Array<{ id: Route; label: string; icon: string; staffOnly?: boolean }> = [
  { id: 'cockpit', label: 'Cockpit', icon: '◧' },
  // Decision 209: quotes going quiet, expiring or expired — was a cockpit panel.
  { id: 'attention', label: 'Needs attention', icon: '⚑' },
  { id: 'quotes', label: 'Quotes', icon: '▤' },
  // Decision 105: the contract book coming up for renewal — Wave9 commercial
  // information, staff only (the endpoints refuse partner sessions too).
  { id: 'renewals', label: 'Renewals', icon: '↻', staffOnly: true },
  // Decision 183: an AGENT sees its own claimable statements here; a Wave9
  // admin sees every agent's. Marked staffOnly so a reseller or internal
  // partner session never sees it, and let through for agents by the render
  // filter below — the API applies both rules independently.
  { id: 'commissions', label: 'Claimable fees', icon: '£', staffOnly: true },
  // Wave9's book, staff only (the API refuses partner sessions too).
]
const LIBRARY_NAV: Array<{ id: Route; label: string; icon: string; staffOnly?: boolean }> = [
  { id: 'catalogue', label: 'Catalogue', icon: '⌗' },
  // Decision 96: templates are for everyone — partners see Wave9-global ones
  // plus their own.
  { id: 'templates', label: 'Templates', icon: '▩' },
  // Decision 111: snippets + tokens on their own page, for everyone — the
  // existing permission rules (67/106) decide what each person can edit.
  { id: 'snippets', label: 'Snippets', icon: '❝' },
  // Decision 93: library management is for EVERY Wave9 internal user. Decision
  // 135 opens it to RESELLER sessions too (their own library + the global one
  // read-only) — the render filter below lets them through; agents still
  // don't see it (their quotes carry Wave9's documents).
  { id: 'library', label: 'Asset library', icon: '▦', staffOnly: true },
  // Decision 97: the portal-wide cost-variation log — cost is staff-only (#53).
  { id: 'costlog', label: 'Cost log', icon: '±', staffOnly: true },
  // Decision 211: the distributor quotes behind one-off Meraki sales — cost, so staff only.
  { id: 'supplierQuotes', label: 'Supplier quotes', icon: '↑', staffOnly: true },
]

/** Admin key only. Hidden from a Wave9 staff session — the endpoints refuse it too. */
const ADMIN_NAV: Array<{ id: Route; label: string; icon: string }> = [
  { id: 'partners', label: 'Partners', icon: '⬢' },
  { id: 'productLines', label: 'Product Lines', icon: '◈' },
  { id: 'organisation', label: 'Organisation', icon: '▣' },
  // Decision 135: the T&Cs library moved out of Organisation to its own page.
  { id: 'terms', label: 'Terms & Conditions', icon: '§' },
  { id: 'jobs', label: 'Scheduled jobs', icon: '⟳' },
  { id: 'readiness', label: 'System status', icon: '◉' },   // decision 173 — was "Build readiness"
  // Decision 205: the monthly board slide — booked, pipeline, notable new
  // (the stage weighting went with decision 245). Mark (after the first live look): "add the
  // pack to the admin section" — board material, so it sits with the admin
  // pages. The API still admits any staff session.
  { id: 'boardPack', label: 'Board pack', icon: '▥' },
  // Decision 245: the ARR planner (#221) is RETIRED — the one ARR forecast is the
  // Wave9 Hub's ARR Cockpit (pipeline + order book + the live ARR base).
  // Decision 213: connect Xero, pick the supplier contact, nominal + tax type.
  { id: 'xero', label: 'Xero', icon: '⌂' },
  // Decision 223: deals handed to billing, their schedules, who is billed and
  // the Create → Xero button. Money-bearing, so admin key only — Mark moved it
  // out of Daily the evening Round B went live ("RBAC applied").
  { id: 'billing', label: 'Billing', icon: '▤' },
  // Decision 224: lines removed / added / re-priced with a reason — month-end view.
  { id: 'billingVariations', label: 'Billing variations', icon: '±' },
]

const MODE_LABEL: Record<Partner['mode'], string> = {
  agent: 'Agent — list-priced, referral fee',
  reseller: 'Reseller — own brand, buy-price floor',
  internal: 'Internal — Wave9 staff, direct pipeline',
}

interface Props {
  route: Route
  onRoute: (r: Route) => void
  role: SessionRole | null
  onSignOut: () => void
  partner: Partner | null
  partners: Partner[]
  onPartner: (id: string) => void
  /** Decision 130: the staff-only all-partners oversight view is active. */
  allView?: boolean
  switching?: boolean
  error?: string | null
  children: ReactNode
}

/** Decision 130: the switcher value for "All partners — overview" (App.tsx owns the sentinel). */
const ALL_PARTNERS = '__all__'

export function AppShell({
  route, onRoute, role, onSignOut, partner, partners, onPartner, allView, switching, error, children,
}: Props) {
  const navButton = (item: { id: Route; label: string; icon: string }) => (
    <button
      key={item.id}
      className={`nav__item ${route === item.id || (item.id === 'quotes' && route === 'builder') ? 'nav__item--on' : ''}`}
      onClick={() => onRoute(item.id)}
      aria-current={route === item.id ? 'page' : undefined}
    >
      <span aria-hidden>{item.icon}</span>
      {item.label}
    </button>
  )

  return (
    <div className="shell">
      <nav className="nav">
        <div className="nav__brand">
          <div className="nav__logo">W9</div>
          <div>
            <strong>Wave9 Quote Portal</strong>
            <span>Quoting — direct &amp; channel</span>
          </div>
        </div>

        <div className="nav__section">Daily</div>
        {DAILY_NAV.filter((i) =>
          !i.staffOnly || role !== null
          // Decision 183: an agent partner session earns the fees, so it sees
          // the claimable page too (staff see every agent's).
          || (i.id === 'commissions' && partner?.mode === 'agent'),
        ).map(navButton)}
        <div className="nav__section">Library</div>
        {/* Decision 135: the Asset library also renders for RESELLER sessions. */}
        {LIBRARY_NAV.filter((i) =>
          !i.staffOnly || role !== null || (i.id === 'library' && partner?.mode === 'reseller'),
        ).map(navButton)}
        {role === 'admin' && (
          <>
            <div className="nav__section">Admin</div>
            {ADMIN_NAV.map(navButton)}
          </>
        )}

        {/* Decision 123 (amended same day — Mark: "promote help & guides to be
            a section"): the guides are IN the app — static files from public/,
            the same zero-backend trick as the pack cover. */}
        <div className="nav__section">Help &amp; guides</div>
        <a className="nav__item" href="/Wave9-Quote-Portal-Options-Guide.pdf" target="_blank" rel="noreferrer">
          <span aria-hidden>ⓘ</span>
          How options work
        </a>
        {role !== null && (
          <a className="nav__item" href="/Wave9-Quote-Portal-Renewals-Guide.pdf" target="_blank" rel="noreferrer">
            <span aria-hidden>↻</span>
            How renewals work
          </a>
        )}
        {/* Decision 190: the STAFF guides are no longer static files — they
            stream from /api/guides/:name to a staff session only, so the
            gate is on the document, not just this link. */}
        {role !== null && (
          <a className="nav__item" href="/api/guides/cheat-sheet" target="_blank" rel="noreferrer">
            <span aria-hidden>☰</span>
            Cheat sheet
          </a>
        )}
        {/* Decision 182: Mark's HubSpot pipeline best-practices guide. Staff
            only — it documents our internal HubSpot hygiene, not anything a
            partner acts on. */}
        {role !== null && (
          <a className="nav__item" href="/api/guides/hubspot-pipeline" target="_blank" rel="noreferrer">
            <span aria-hidden>◴</span>
            HubSpot pipeline best practice
          </a>
        )}
        {/* 2026-09-16: the Billing guide — how invoicing works now Invoice Stack
            has gone. Staff only, like the other guides; the Billing page itself
            stays admin-only. */}
        {role !== null && (
          <a className="nav__item" href="/api/guides/billing" target="_blank" rel="noreferrer">
            <span aria-hidden>▤</span>
            Billing guide
          </a>
        )}

        <div className="nav__spacer" />

        <div className="nav__foot">
          <label htmlFor="partner-switch" style={{ display: 'block', marginBottom: 5 }}>
            {/* Decision 136: partner sessions have exactly one organisation. */}
            {role !== null ? 'Acting as' : 'Your organisation'}
          </label>
          <select
            id="partner-switch"
            className="select"
            style={{ width: '100%' }}
            value={allView ? ALL_PARTNERS : (partner?.id ?? '')}
            disabled={switching || partners.length === 0}
            onChange={(e) => onPartner(e.target.value)}
          >
            <option value="" disabled>
              {partners.length === 0 ? 'No partners yet' : 'Choose a partner…'}
            </option>
            {/* Decision 130: staff only — a view over every partner's quotes,
                not an act-as (partner sessions never see this option). */}
            {role !== null && <option value={ALL_PARTNERS}>All partners — overview</option>}
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.isTest ? ' [test]' : ''}
              </option>
            ))}
          </select>
          <p style={{ marginTop: 7 }}>
            {switching ? 'Switching…' : allView ? 'Oversight — every partner\'s quotes' : partner ? MODE_LABEL[partner.mode] : 'Nothing selected'}
          </p>
          {error && <p style={{ color: '#ffd9dc' }}>{error}</p>}
          <p style={{ marginTop: 10 }}>
            {/* Decision 136: partner sessions are labelled as themselves, not "Wave9 staff". */}
            {role === 'admin' ? 'Wave9 admin' : role !== null ? 'Wave9 staff' : partner ? `${partner.name}` : 'Signed in'}
            {' · '}
            <button className="nav__link" onClick={onSignOut}>Sign out</button>
          </p>
        </div>
      </nav>

      <main className="main">{children}</main>
    </div>
  )
}
